'use client';

import { useTranslations } from 'next-intl';
import { parseAsString, useQueryState } from 'nuqs';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Heading, type HeadingData } from '@/components/cms/Heading';
import { FreeTextContent } from '@/components/cms/FreeTextContent';
import { PriceTable } from '@/components/service/PriceTable';
import { ServiceSerialNumberInput } from '@/components/service/ServiceSerialNumberInput';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { SectionLoadingOverlay } from '@/components/ui/SectionLoadingOverlay';
import { BRAND_NAME } from '@/lib/const';
import { getCountryFromLocale } from '@/lib/i18n/config';
import { useRouter } from '@/lib/i18n/navigation';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import {
  fetchRecommendPrice,
  RECOMMEND_PRICE_GENERIC_FAILURE,
} from '@/lib/service/recommendPriceClient';
import { loadRepairWatchContext } from '@/lib/service/loadRepairWatchContext';
import { buildRepairRequestWatchPath } from '@/lib/service/repairRequestQuery';
import { resolveWatchReference } from '@/lib/service/resolveWatchReference';
import { repairRequestActions } from '@/lib/service/repairRequestState';
import type { Watch } from '@/lib/myWatches/types';
import { SERVICE_PRICE_RESULTS_RICH_TEXT_FORMATTERS } from '@/lib/service/servicePriceResultsFormatters';
import { buildServicePriceTableLabels } from '@/lib/service/servicePriceTableLabels';
import type { RecommendPrice, RecommendPriceResponse } from '@/lib/service/types';
import { modalActions } from '@/states/modalState';

import styles from './ServicePriceLookup.module.scss';

export type ServicePriceLookupActions = 'search' | 'repairRequest';

/** `serialOnly` is for repair request flows; `serialAndReference` is for CMS price lookup. */
export type ServicePriceLookupIdentificationMode = 'serialOnly' | 'serialAndReference';

export interface ServicePriceLookupProps {
  actions?: ServicePriceLookupActions;
  identificationMode?: ServicePriceLookupIdentificationMode;
  fetchInitialEmptyRef?: boolean;
  initialData?: RecommendPriceResponse | null;
  initialError?: string | null;
  initialRefNumber?: string;
  locale: string;
  placeholderHeading?: HeadingData | null;
  subtext?: { json?: unknown } | null;
  testId?: string;
  showTitle?: boolean;
}

type IdentifiedWatch = {
  serialNumber: string | null;
  referenceNumber: string;
  watchProductSummary: WatchProductSummary;
  watchWarranty: Watch | null;
};

function ServicePriceLookupTitle({
  showTitle,
  placeholderHeading,
  defaultTitle,
}: Readonly<{
  showTitle: boolean;
  placeholderHeading?: HeadingData | null;
  defaultTitle: string;
}>): ReactNode {
  if (!showTitle) return null;
  if (placeholderHeading) {
    return <Heading Heading={placeholderHeading} />;
  }
  return <h2 className={styles.title}>{defaultTitle}</h2>;
}

function ServicePriceLookupIntro({
  subtext,
  defaultIntro,
}: Readonly<{
  subtext?: { json?: unknown } | null;
  defaultIntro: string;
}>): ReactNode {
  if (subtext?.json) {
    return <FreeTextContent className={styles.intro} freeTextContent={subtext} />;
  }
  return <p className={styles.intro}>{defaultIntro}</p>;
}

async function lookupWatchPrices(
  value: string,
  locale: string,
  countryCode: string,
  serialOnly: boolean
): Promise<
  | { status: 'not-found'; serialNumber?: string }
  | { status: 'error' }
  | { status: 'identified'; identifiedWatch: IdentifiedWatch; priceResult: RecommendPriceResponse }
> {
  const resolved = await resolveWatchReference({
    input: value,
    locale,
    countryCode,
    serialOnly,
  });

  if (resolved.status === 'not-found') {
    return { status: 'not-found', serialNumber: resolved.serialNumber };
  }

  if (resolved.status === 'error') {
    return { status: 'error' };
  }

  const priceResult = await fetchRecommendPrice({
    countryCode,
    locale,
    watchReference: resolved.referenceNumber,
  });

  return {
    status: 'identified',
    identifiedWatch: {
      serialNumber: resolved.serialNumber ?? null,
      referenceNumber: resolved.referenceNumber,
      watchProductSummary: resolved.watchProductSummary,
      watchWarranty: resolved.watchWarranty ?? null,
    },
    priceResult,
  };
}

type LookupSubmitHandlers = {
  clearSearchResults: () => void;
  setRequestError: (message: string | null) => void;
  setIdentifiedWatch: (watch: IdentifiedWatch | null) => void;
  applyPriceResult: (result: RecommendPriceResponse) => void;
  setRefNumberParam: (value: string) => Promise<unknown>;
  openWatchNotFoundModal: (serialNumber: string) => void;
  requestFailedMessage: string;
};

async function processLookupNotFound(
  value: string,
  serialNumber: string | undefined,
  isSerialOnly: boolean,
  isRepairRequest: boolean,
  handlers: LookupSubmitHandlers
): Promise<void> {
  handlers.clearSearchResults();
  handlers.setRequestError(null);
  if (!isSerialOnly) {
    await handlers.setRefNumberParam(value);
  }
  if (isRepairRequest) {
    handlers.openWatchNotFoundModal(serialNumber ?? value);
  }
}

async function processLookupIdentified(
  value: string,
  identifiedWatch: IdentifiedWatch,
  priceResult: RecommendPriceResponse,
  isSerialOnly: boolean,
  handlers: LookupSubmitHandlers
): Promise<void> {
  handlers.setIdentifiedWatch(identifiedWatch);
  handlers.applyPriceResult(priceResult);
  if (!isSerialOnly) {
    await handlers.setRefNumberParam(value);
  }
}

function mapLookupSubmitError(error: unknown, requestFailedMessage: string): string {
  const message = error instanceof Error ? error.message : '';
  return message && message !== RECOMMEND_PRICE_GENERIC_FAILURE ? message : requestFailedMessage;
}

interface ServicePriceLookupResultsProps {
  showNoWatch: boolean;
  showResults: boolean;
  requestServiceButton: ReactNode;
  noWatchFoundMessage: string;
  resultsHeading: ReactNode;
  prices: RecommendPrice[];
  labels: ReturnType<typeof buildServicePriceTableLabels>;
  locale: string;
  packShipIncluded: boolean;
  vatIncluded: boolean;
}

function ServicePriceLookupResults({
  showNoWatch,
  showResults,
  requestServiceButton,
  noWatchFoundMessage,
  resultsHeading,
  prices,
  labels,
  locale,
  packShipIncluded,
  vatIncluded,
}: Readonly<ServicePriceLookupResultsProps>): ReactNode {
  if (showNoWatch) {
    return (
      <div className={styles.results}>
        <p className={styles.empty} data-testid="tableTitle">
          {noWatchFoundMessage}
        </p>
        {requestServiceButton}
      </div>
    );
  }

  if (!showResults) {
    return null;
  }

  return (
    <div className={styles.results}>
      <div className={styles.tableTitle}>
        <h2 className={styles.resultsHeading} data-testid="tableTitle">
          {resultsHeading}
        </h2>

        {prices.length > 0 ? (
          <PriceTable
            labels={labels}
            locale={locale}
            packShipIncluded={packShipIncluded}
            prices={prices}
            vatIncluded={vatIncluded}
          />
        ) : null}

        {requestServiceButton}
      </div>
    </div>
  );
}

export function ServicePriceLookup({
  actions = 'search',
  identificationMode = 'serialAndReference',
  fetchInitialEmptyRef = true,
  initialData,
  initialError,
  initialRefNumber = '',
  locale,
  placeholderHeading,
  subtext,
  testId,
  showTitle = true,
}: Readonly<ServicePriceLookupProps>): React.ReactNode {
  const t = useTranslations('servicePrices');
  const tLanding = useTranslations('service.repairRequest.landing');
  const tIdentifyModal = useTranslations('service.repairRequest.watch.identifyModal');
  const router = useRouter();
  const countryCode = getCountryFromLocale(locale).toUpperCase();
  const isSerialOnly = identificationMode === 'serialOnly';
  const [, setRefNumberParam] = useQueryState(
    'refNumber',
    parseAsString.withOptions({ shallow: true, history: 'push' })
  );

  const [number, setNumber] = useState(initialRefNumber);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [productName, setProductName] = useState<string | null>(initialData?.model ?? null);
  const [prices, setPrices] = useState<RecommendPrice[]>(initialData?.prices ?? []);
  const [vatIncluded, setVatIncluded] = useState(Boolean(initialData?.vatIncluded));
  const [packShipIncluded, setPackShipIncluded] = useState(Boolean(initialData?.packShipIncluded));
  const [searchNumber, setSearchNumber] = useState(initialRefNumber);
  const [isProcessing, setIsProcessing] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(initialError ?? null);
  const [identifiedWatch, setIdentifiedWatch] = useState<IdentifiedWatch | null>(null);

  const isRepairRequest = actions === 'repairRequest';
  const isBusy = isProcessing;
  const labels = useMemo(() => buildServicePriceTableLabels(t, countryCode), [countryCode, t]);
  const resultsHeading = useMemo(
    () =>
      productName
        ? t.rich('resultsFor', {
            productName: productName.split(',')[0],
            ...SERVICE_PRICE_RESULTS_RICH_TEXT_FORMATTERS,
          })
        : t('catalogueTitle', { brand: BRAND_NAME }),
    [productName, t]
  );
  const showNoWatch =
    !isBusy &&
    !requestError &&
    Boolean(searchNumber) &&
    !productName &&
    prices.length === 0 &&
    !(isRepairRequest && identifiedWatch !== null);
  const hasResultContent = Boolean(productName) || prices.length > 0 || identifiedWatch !== null;
  const showResults =
    !isBusy &&
    !requestError &&
    !showNoWatch &&
    (hasResultContent || (fetchInitialEmptyRef && prices.length > 0));

  const navigateToWatchStep = (context: {
    serialNumber: string | null;
    referenceNumber: string | null;
    watchWarranty: Watch | null;
    watchProductSummary: WatchProductSummary | null;
  }) => {
    repairRequestActions.clearWatchContext();
    repairRequestActions.setWatchContext(context);
    router.push(buildRepairRequestWatchPath(context.serialNumber));
  };

  const navigateToWatch = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      navigateToWatchStep({
        serialNumber: null,
        referenceNumber: null,
        watchWarranty: null,
        watchProductSummary: null,
      });
      return;
    }

    const context = await loadRepairWatchContext({
      serialNumber: trimmed,
      locale,
      countryCode,
      serialOnly: isSerialOnly,
    });
    navigateToWatchStep({
      serialNumber: context.serialNumber,
      referenceNumber: context.referenceNumber,
      watchWarranty: context.watchWarranty,
      watchProductSummary: context.watchProductSummary,
    });
  };

  const openWatchNotFoundModal = (serialNumber: string) => {
    modalActions.open('watch-not-found', {
      serialNumber,
      onOrderService: (attemptedSerialNumber) => {
        const trimmed = attemptedSerialNumber?.trim();
        navigateToWatchStep({
          serialNumber: trimmed || null,
          referenceNumber: null,
          watchWarranty: null,
          watchProductSummary: null,
        });
      },
    });
  };

  const clearSearchResults = () => {
    setPrices([]);
    setProductName(null);
    setVatIncluded(false);
    setPackShipIncluded(false);
  };

  const applyPriceResult = (result: RecommendPriceResponse) => {
    setProductName(result.model ?? null);
    setPrices(result.prices ?? []);
    setVatIncluded(Boolean(result.vatIncluded));
    setPackShipIncluded(Boolean(result.packShipIncluded));
    setRequestError(null);
  };

  const lookupSubmitHandlers: LookupSubmitHandlers = {
    clearSearchResults,
    setRequestError,
    setIdentifiedWatch,
    applyPriceResult,
    setRefNumberParam,
    openWatchNotFoundModal,
    requestFailedMessage: t('requestFailed'),
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = number.trim();
    if (!value) {
      setFieldError(isSerialOnly ? t('serialNumberRequired') : t('numberRequired'));
      return;
    }

    setFieldError(null);
    setSearchNumber(value);
    setIdentifiedWatch(null);
    setIsProcessing(true);
    try {
      const lookup = await lookupWatchPrices(value, locale, countryCode, isSerialOnly);

      if (lookup.status === 'not-found') {
        await processLookupNotFound(
          value,
          lookup.serialNumber,
          isSerialOnly,
          isRepairRequest,
          lookupSubmitHandlers
        );
        return;
      }

      if (lookup.status === 'error') {
        clearSearchResults();
        setRequestError(t('requestFailed'));
        return;
      }

      await processLookupIdentified(
        value,
        lookup.identifiedWatch,
        lookup.priceResult,
        isSerialOnly,
        lookupSubmitHandlers
      );
    } catch (error) {
      clearSearchResults();
      setRequestError(mapLookupSubmitError(error, t('requestFailed')));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = async () => {
    setIsProcessing(true);
    try {
      await navigateToWatch('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRequestService = async () => {
    setIsProcessing(true);
    try {
      if (identifiedWatch) {
        navigateToWatchStep({
          serialNumber: identifiedWatch.serialNumber,
          referenceNumber: identifiedWatch.referenceNumber,
          watchWarranty: identifiedWatch.watchWarranty,
          watchProductSummary: identifiedWatch.watchProductSummary,
        });
        return;
      }

      const attemptedSerialNumber = (searchNumber || number).trim();
      navigateToWatchStep({
        serialNumber: attemptedSerialNumber || null,
        referenceNumber: null,
        watchWarranty: null,
        watchProductSummary: null,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const inputLabel = isSerialOnly ? t('serialNumber') : t('serialNumberOrSku');

  const requestServiceButton =
    isRepairRequest && Boolean(searchNumber) ? (
      <div className={styles.resultsActions}>
        <Button
          className={styles.searchButton}
          disabled={isProcessing}
          iconAfter={<Icon aria-hidden="true" name="chevron-right" />}
          loading={isProcessing}
          onClick={() => void handleRequestService()}
          type="button"
          variant="secondary"
          data-testid="service-request-submit"
        >
          {tLanding('requestService')}
        </Button>
      </div>
    ) : null;

  return (
    <section className={styles.root} data-testid={testId} aria-busy={isBusy}>
      <div className={styles.search}>
        <div className={styles.header}>
          <ServicePriceLookupTitle
            showTitle={showTitle}
            placeholderHeading={placeholderHeading}
            defaultTitle={isSerialOnly ? tIdentifyModal('title') : t('searchTitle')}
          />
          <ServicePriceLookupIntro
            subtext={subtext}
            defaultIntro={
              isSerialOnly ? tIdentifyModal('hint', { brand: BRAND_NAME }) : t('searchIntro')
            }
          />
        </div>

        <div className={styles.formBlock}>
          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            <ServiceSerialNumberInput
              error={fieldError}
              label={inputLabel}
              name="number"
              onChange={(event) => {
                setNumber(event.target.value);
                if (fieldError) setFieldError(null);
              }}
              value={number}
            />
            <div className={styles.formActions}>
              {isRepairRequest ? (
                <Button
                  className={styles.searchButton}
                  disabled={isProcessing}
                  iconAfter={<Icon aria-hidden="true" name="chevron-right" />}
                  onClick={() => void handleSkip()}
                  type="button"
                  variant="outline"
                  data-testid="service-request-skip"
                >
                  {tLanding('skip')}
                </Button>
              ) : null}
              <Button
                className={styles.searchButton}
                iconAfter={<Icon aria-hidden="true" name="chevron-right" />}
                loading={isProcessing}
                type="submit"
                variant="secondary"
                data-testid={isRepairRequest ? 'service-request-continue' : undefined}
              >
                {t('search')}
              </Button>
            </div>
          </form>
          {isSerialOnly ? null : (
            <button
              className={styles.hintLink}
              onClick={() => modalActions.open('service-reference-hint')}
              type="button"
            >
              <span>{t('findSnAndRnLink')}</span>
              <Icon aria-hidden="true" name="chevron-right" />
            </button>
          )}
        </div>
      </div>

      {isBusy ? (
        <SectionLoadingOverlay isLoading transparent>
          <div className={styles.loaderPlaceholder} aria-hidden="true" />
        </SectionLoadingOverlay>
      ) : null}

      {requestError && !isBusy ? <p className={styles.error}>{requestError}</p> : null}

      <ServicePriceLookupResults
        showNoWatch={showNoWatch}
        showResults={showResults}
        requestServiceButton={requestServiceButton}
        noWatchFoundMessage={t('noWatchFound')}
        resultsHeading={resultsHeading}
        prices={prices}
        labels={labels}
        locale={locale}
        packShipIncluded={packShipIncluded}
        vatIncluded={vatIncluded}
      />
    </section>
  );
}

ServicePriceLookup.displayName = 'ServicePriceLookup';
